/**
 * Opaque, branded entity identifier used across the domain model.
 *
 * `EntityId` values are plain strings at runtime (so they serialize cleanly into the canonical
 * project document) but are branded at the type level to prevent accidental mixing with arbitrary
 * strings.
 */
export type EntityId = string & { readonly __brand: 'EntityId' };

/** Cast an existing string (e.g. loaded from a project file) into an {@link EntityId}. */
export function toEntityId(value: string): EntityId {
  return value as EntityId;
}

/**
 * Monotonic-ish unique id generator. Prefers `crypto.randomUUID` when available and falls back to a
 * timestamp + counter + random scheme for environments where it is not.
 */
export class EntityIdGenerator {
  private counter = 0;

  constructor(private readonly prefix = '') {}

  next(): EntityId {
    this.counter += 1;
    const cryptoObj = (globalThis as { crypto?: Crypto }).crypto;
    if (cryptoObj && typeof cryptoObj.randomUUID === 'function') {
      return toEntityId(`${this.prefix}${cryptoObj.randomUUID()}`);
    }
    const time = Date.now().toString(36);
    const rand = Math.floor(Math.random() * 0xffffff).toString(36);
    const seq = this.counter.toString(36);
    return toEntityId(`${this.prefix}${time}-${seq}-${rand}`);
  }
}

/** Shared default generator for ad-hoc id creation. */
export const defaultIdGenerator = new EntityIdGenerator();

/** Convenience helper that produces a fresh {@link EntityId} from the default generator. */
export function createEntityId(prefix?: string): EntityId {
  return prefix ? new EntityIdGenerator(prefix).next() : defaultIdGenerator.next();
}
